import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSprings } from 'react-spring';

import { range, normalize, clamp } from '@/utils';
import useMousePositionWithinElement from '@/hooks/use-mouse-position-within-element';

const BAR_WIDTH = 6;
const BAR_HEIGHT = 32;
const BAR_GAP = 1;
const MIN_SCALE = 1 / BAR_WIDTH / 2;
const MAX_SCALE = 1;
const MIN_OPACITY = 0.1;
const MAX_OPACITY = 0.75;
const SPRING_CONFIG = {
  tension: 300,
  friction: 20,
};

interface Props {
  numOfBars?: number;
  onClick?: () => void;
}

function LogoDoodle({ numOfBars = 18, onClick }: Props) {
  const wrapperRef = React.useRef<HTMLButtonElement>(null);
  const [status, setStatus] = React.useState('static');

  const onEnterBox = React.useCallback(() => {
    setStatus('by-mouse-position');
  }, []);
  const onLeaveBox = React.useCallback(() => {
    setStatus('static');
  }, []);

  const [mousePos, box] = useMousePositionWithinElement(wrapperRef, {
    buffer: 96,
    onEnterBox,
    onLeaveBox,
  });

  let barScales;
  if (status === 'by-mouse-position') {
    if (mousePos.x !== null && box !== null) {
      barScales = calculateInteractiveBarScales({
        numOfBars,
        xPosition: mousePos.x,
        box,
      });
    }
  } else if (status === 'active') {
    barScales = calculateActiveBarScales(numOfBars);
  } else {
    barScales = calculateStaticBarScales(numOfBars);
  }

  // @ts-ignore
  const springs = useSprings(numOfBars, barScales);

  return (
    <Wrapper
      ref={wrapperRef}
      onMouseDown={() => setStatus('active')}
      onMouseUp={() => setStatus('by-mouse-position')}
      onMouseLeave={() => setStatus('by-mouse-position')}
      onClick={onClick}
    >
      {springs.map((style, index) => (
        // IDK why TS doesn't like this and IDC
        // @ts-ignore
        <Bar key={index} style={style} />
      ))}
    </Wrapper>
  );
}

function calculateStaticBarScales(numOfBars: number) {
  return range(numOfBars).map((index) => {
    const scale = normalize(
      index,
      0,
      numOfBars - 1,
      MIN_SCALE,
      MAX_SCALE
    );
    const opacity = normalize(
      index,
      0,
      numOfBars - 1,
      MIN_OPACITY,
      MAX_OPACITY
    );
    return {
      transform: `scaleX(${scale})`,
      opacity,
      config: SPRING_CONFIG,
    };
  });
}

function calculateActiveBarScales(numOfBars: number) {
  return range(numOfBars).map((index) => {
    return {
      transform: `scaleX(1)`,
      opacity: 1,
      config: {
        tension: 400,
        friction: 10,
      },
    };
  });
}

function calculateInteractiveBarScales({
  numOfBars,
  xPosition,
  box,
}: {
  numOfBars: number;
  xPosition: number;
  box: DOMRect;
}) {
  return range(numOfBars).map((index) => {
    const centerX =
      index * BAR_WIDTH + index * BAR_GAP + BAR_WIDTH / 2;

    const distance = Math.abs(xPosition - centerX);

    let scale = normalize(
      distance,
      0,
      box.width * 0.8,
      MAX_SCALE,
      MIN_SCALE
    );
    scale = clamp(scale, MIN_SCALE, MAX_SCALE);

    let opacity = normalize(
      distance,
      0,
      box.width * 0.8,
      MAX_OPACITY,
      MIN_OPACITY
    );
    opacity = clamp(scale, MIN_OPACITY, MAX_OPACITY);

    return {
      transform: `scaleX(${scale})`,
      opacity,
      config: SPRING_CONFIG,
    };
  });
}

const Wrapper = styled.button`
  display: flex;
  gap: ${BAR_GAP}px;
  user-select: none;
`;

const Bar = styled(animated.div)`
  height: ${BAR_HEIGHT}px;
  width: ${BAR_WIDTH}px;
  background: white;
  transform-origin: center center;
  will-change: transform;

  &:first-of-type {
    border-radius: ${BAR_WIDTH / 2}px 0 0 ${BAR_WIDTH / 2}px;
  }
  &:last-of-type {
    border-radius: 0 ${BAR_WIDTH / 2}px ${BAR_WIDTH / 2}px 0;
  }
`;

export default LogoDoodle;
